import React from 'react';
import { I18n } from '@aws-amplify/core';
import { Row, Col, Collapse } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';

import { useRosaResultContext } from '../context';
import { RosaCategoryEnum } from '../../Evaluation/constants';
import type { SubCategory, Section, SectionItem } from '../types';
import * as S from './styles';

const DURATION = 'duration';
const SEAT_BACKREST = 'seat_backrest';
const CHAIR = RosaCategoryEnum.CHAIR;
const MONITOR_PHONE = RosaCategoryEnum.MONITOR_PHONE;
const MOUSE_KEYBOARD = RosaCategoryEnum.MOUSE_KEYBOARD;

export function SectionComponent() {
	const {
		rosaResult: {
			scores: { section_a, section_b, section_c },
			sections
		}
	} = useRosaResultContext();

	function capitalizeWords(value: string): string {
		return value
			.replace('_', ' ')
			.split(' ')
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
			.join(' ');
	}

	function handleFormatScore(score: number): string | number {
		if (score === 1) return '+ 1';
		return score;
	}

	function handleDurationLabelSubItems(subItem: SubCategory): string {
		return subItem.type === DURATION ? I18n.get('Duration') : I18n.get(subItem.description);
	}

	const handleExpandIconCollapse = (value?: boolean): JSX.Element => {
		const style = { marginTop: '12px' };
		return value ? <UpOutlined style={style} /> : <DownOutlined style={style} />;
	};

	function handleSectionSubCategories(name: string): SubCategory[][] {
		return sections?.find((section: Section) => section.name === name)?.sub_categories ?? [];
	}

	function isDurationInternal(subItem: SubCategory): boolean {
		return (subItem.type === DURATION || subItem.sequence === 0) && subItem.sub_category !== SEAT_BACKREST;
	}

	function getDurationScoreSubcategory(item: SectionItem, subCategory: string) {
		for (const subcat of item.children) {
			const found = subcat.find((subItem) => subItem.type === DURATION && subItem.sub_category === subCategory);
			if (found) return found.score;
		}

		return 0;
	}

	function isDurationExternal(item: SectionItem): boolean {
		return item.key === '1';
	}

	function isSubitemWithSectionDuration(subItems: SubCategory[]): boolean {
		const subcategoryWithSectionDuration: Partial<SubCategory> = {
			sub_category: SEAT_BACKREST,
			type: DURATION
		};
		return subItems.some(
			(value) =>
				value.sub_category === subcategoryWithSectionDuration.sub_category &&
				value.type === subcategoryWithSectionDuration.type
		);
	}

	const items: SectionItem[] = [
		{
			key: '1',
			label: `${I18n.get('Section')} A`,
			score: section_a,
			children: handleSectionSubCategories(CHAIR)
		},
		{
			key: '2',
			label: `${I18n.get('Section')} B`,
			score: section_b,
			children: handleSectionSubCategories(MONITOR_PHONE)
		},
		{
			key: '3',
			label: `${I18n.get('Section')} C`,
			score: section_c,
			children: handleSectionSubCategories(MOUSE_KEYBOARD)
		}
	];
	return (
		<Row justify="center" gutter={[0, 0]}>
			<Col xs={24}>
				<S.StyledCollapse
					ghost
					defaultActiveKey={['1']}
					expandIcon={({ isActive }) => handleExpandIconCollapse(isActive)}
				>
					{items?.map((item: SectionItem) => (
						<Collapse.Panel
							key={item.key}
							header={
								<Row justify="space-between">
									<Col>
										<S.SectionTitle>
											<span>{I18n.get(item.label)}</span>
										</S.SectionTitle>
									</Col>
									<Col>
										<S.ScoreComponent>
											<S.ScoreText>{item.score}</S.ScoreText>
										</S.ScoreComponent>
									</Col>
								</Row>
							}
						>
							{item?.children?.map((subItems: SubCategory[], subKey: number) => (
								<S.SectionContainer key={subKey}>
									<Row justify="space-between">
										<Col>
											<h2>
												<strong>{I18n.get(capitalizeWords(subItems[0].sub_category))}</strong>
											</h2>
										</Col>
										<Col>
											<S.ScoreComponent>
												<S.ScoreText>
													{(isSubitemWithSectionDuration(subItems)
														? subItems.filter((subItem) => subItem.type !== DURATION)
														: subItems
													).reduce((acc, subItem) => acc + subItem.score, 0)}{' '}
												</S.ScoreText>
											</S.ScoreComponent>
										</Col>
									</Row>
									<div style={{ marginTop: '1rem', marginBottom: '0' }}>
										{subItems?.map((subItem: SubCategory, index: number) => (
											<div key={index}>
												{isDurationInternal(subItem) ? (
													<Row justify="end">
														<S.RightAlignedElement
															style={{ marginTop: subItem.sequence === 0 ? '-18px' : 0 }}
														>
															<Col>
																<S.DurationLabel>
																	{handleDurationLabelSubItems(subItem)}
																</S.DurationLabel>
															</Col>
															<S.DurationScoreCol lg={8}>
																<h3 style={{ marginTop: '0.5rem' }}>
																	{handleFormatScore(subItem.score)}
																</h3>
															</S.DurationScoreCol>
														</S.RightAlignedElement>
													</Row>
												) : subItem.type !== DURATION ? (
													<S.DescriptionElement>
														<Col lg={22}>
															<S.ElementDescription>
																{I18n.get(subItem.description)}
															</S.ElementDescription>
														</Col>
														<S.ElementStyledCol lg={2}>
															<h3 style={{ marginTop: '0.5rem' }}>
																{!subItem.required && '+ '}
																{subItem.score}
															</h3>
														</S.ElementStyledCol>
													</S.DescriptionElement>
												) : null}
											</div>
										))}
									</div>
								</S.SectionContainer>
							))}
							{isDurationExternal(item) && (
								<S.SectionContainer>
									<Row justify="space-between">
										<Col>
											<h2>
												<strong>{I18n.get(capitalizeWords(DURATION))}</strong>
											</h2>
										</Col>
										<Col>
											<S.ScoreComponent>
												<S.ScoreText>
													{getDurationScoreSubcategory(item, SEAT_BACKREST)}
												</S.ScoreText>
											</S.ScoreComponent>
										</Col>
									</Row>
								</S.SectionContainer>
							)}
						</Collapse.Panel>
					))}
				</S.StyledCollapse>
			</Col>
		</Row>
	);
}
